import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useGetAllTasksByEntityQuery } from "store/apis/filing";
import { useGetBaseTaskTemplatesQuery } from "store/apis/getTemplates";
import { isStateComplianceDeadlineTask } from "util/isStateComplianceDeadlineTask";
import {
  STATE_ANNUAL_REPORTS,
  STATE_FILING_CALIFORNIA,
  STATE_FRANCHISE_TAX_DELAWARE,
} from "types/Models/services";

export const useStateDeadlineTasks = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = query.get("entity");

  const currentYear = dayjs().year();
  const prevYear = (currentYear - 1).toString();
  const thisYear = currentYear.toString();

  const { data: prevYearData } = useGetAllTasksByEntityQuery(
    {
      groupId: groupId!,
      entity_id: entityId!,
      filing_season: prevYear,
    },
    { skip: !groupId || !entityId }
  );

  const { data: currentYearData } = useGetAllTasksByEntityQuery(
    {
      groupId: groupId!,
      entity_id: entityId!,
      filing_season: thisYear,
    },
    { skip: !groupId || !entityId }
  );

  const { data: baseTemplatesData } = useGetBaseTaskTemplatesQuery({});
  const allTemplates = Object.values(baseTemplatesData ?? {}).flat();

  const annualReportTemplateId = allTemplates.find(
    (t) => t.base_task_key === STATE_ANNUAL_REPORTS
  )?.uuid;

  const franchiseTaxDelawareTemplateId = allTemplates.find(
    (t) => t.base_task_key === STATE_FRANCHISE_TAX_DELAWARE
  )?.uuid;

  const californiaFilingTemplateId = allTemplates.find(
    (t) => t.base_task_key === STATE_FILING_CALIFORNIA
  )?.uuid;

  const relevantTemplateIds = [
    annualReportTemplateId,
    franchiseTaxDelawareTemplateId,
    californiaFilingTemplateId,
  ].filter((id): id is string => Boolean(id));

  const allTasks = [
    ...(prevYearData?.tasks ?? []),
    ...(currentYearData?.tasks ?? []),
  ].filter((t) => isStateComplianceDeadlineTask(t, relevantTemplateIds));

  const activeTasks = allTasks.filter(
    (t) =>
      t.deadline_date &&
      !t.is_archived &&
      !("is_final" in t.current_state && t.current_state.is_final)
  );

  const nextTask = [...activeTasks].sort(
    (a, b) =>
      dayjs(a.deadline_date!).valueOf() - dayjs(b.deadline_date!).valueOf()
  )[0];

  const eligibleTasks = allTasks.filter(
    (t) => t.deadline_date && !t.is_archived
  );

  const getDeadlineForState = (stateName: string) => {
    const activeStateTasks = activeTasks.filter((t) =>
      t.task_title?.includes(stateName)
    );
    if (activeStateTasks.length > 0) {
      return activeStateTasks.sort(
        (a, b) =>
          dayjs(a.deadline_date!).valueOf() - dayjs(b.deadline_date!).valueOf()
      )[0];
    }
    return eligibleTasks
      .filter((t) => t.task_title?.includes(stateName))
      .sort(
        (a, b) =>
          dayjs(b.deadline_date!).valueOf() - dayjs(a.deadline_date!).valueOf()
      )[0];
  };

  return { nextTask, getDeadlineForState };
};
